import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Polygon, Popup, LayersControl, Marker, useMap, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useI18n } from '../i18n';

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Helper component to fly to highlighted parcel bounds
const FlyToBounds = ({ highlightedParcel }) => {
  const map = useMap();
  useEffect(() => {
    if (highlightedParcel) {
      const allCoords = [
        ...highlightedParcel.coordinates,
        ...(highlightedParcel.additionalCoordinates ? highlightedParcel.additionalCoordinates.flat() : [])
      ];
      const bounds = L.latLngBounds(allCoords.map(([lat, lng]) => [lat, lng]));
      map.flyToBounds(bounds, { padding: [80, 80], maxZoom: 17, duration: 1 });
    }
  }, [highlightedParcel, map]);
  return null;
};

// Reset view control component
const ResetViewControl = ({ bounds, onReset }) => {
  const { t } = useI18n();
  const map = useMap();

  const handleReset = useCallback(() => {
    map.flyToBounds(bounds, { padding: [40, 40], duration: 0.8 });
    if (onReset) onReset();
  }, [map, bounds, onReset]);

  return (
    <div className="leaflet-top leaflet-left map-reset-control">
      <div className="leaflet-control">
        <button
          className="map-reset-btn"
          onClick={handleReset}
          title={t('map_reset_view')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 2v5h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 8.5a5.5 5.5 0 1 0 1.3-3.6L2 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {t('map_reset_view')}
        </button>
      </div>
    </div>
  );
};

// Premium status-based color palette aligned with brand identity (#2a0a18)
const STATUS_COLORS = {
  available: { stroke: '#8B6914', fill: '#C9A227' },   // Warm gold — signals opportunity & value
  sold:      { stroke: '#7A7D82', fill: '#A8ADB3' },   // Neutral slate — de-emphasised, settled
  selected:  { stroke: '#2a0a18', fill: '#5C1A2A' },   // Brand burgundy — active focus
};

// Map legend component – collapsible on mobile
const MapLegend = () => {
  const { t } = useI18n();
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div className={`map-legend ${collapsed ? 'map-legend-collapsed' : ''}`}>
      <button
        className="map-legend-toggle"
        onClick={() => setCollapsed(!collapsed)}
        aria-label="Toggle legend"
      >
        <span className="map-legend-toggle-label">{t('map_legend_title')}</span>
        <svg
          className={`map-legend-chevron ${collapsed ? '' : 'open'}`}
          width="12" height="12" viewBox="0 0 12 12"
        >
          <polyline points="2,4 6,8 10,4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div className="map-legend-body">
        <h4 className="map-legend-title-desktop">{t('map_legend_title')}</h4>
        <div className="map-legend-items">
          <div className="map-legend-item">
            <span className="map-legend-swatch" style={{ background: STATUS_COLORS.available.fill, border: `2px solid ${STATUS_COLORS.available.stroke}` }}></span>
            <span>{t('map_legend_available')}</span>
          </div>
          <div className="map-legend-item">
            <span className="map-legend-swatch" style={{ background: STATUS_COLORS.sold.fill, border: `2px solid ${STATUS_COLORS.sold.stroke}` }}></span>
            <span>{t('map_legend_sold')}</span>
          </div>
          <div className="map-legend-item">
            <span className="map-legend-swatch" style={{ background: STATUS_COLORS.selected.fill, border: `2px solid ${STATUS_COLORS.selected.stroke}` }}></span>
            <span>{t('map_legend_selected')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const InteractiveMap = ({ parcels, onParcelSelect, highlightedParcel }) => {
  const { t } = useI18n();

  // Detect mobile / touch so we can skip popups on small screens
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  // Compute bounds of all parcel coordinates so initial view shows all clusters
  const allCoords = parcels.flatMap(p => [
    ...p.coordinates,
    ...(p.additionalCoordinates ? p.additionalCoordinates.flat() : [])
  ]);
  const bounds = L.latLngBounds(allCoords.map(([lat, lng]) => [lat, lng]));

  // Create custom SVG-based div icons matching premium status colors
  const availableIcon = useMemo(() => new L.DivIcon({
    className: 'custom-parcel-marker available-marker',
    html: `<div class="marker-pin available-pin"><svg width="14" height="14" viewBox="0 0 14 14"><circle cx="7" cy="7" r="5.5" fill="${STATUS_COLORS.available.fill}" stroke="#fff" stroke-width="2"/></svg></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  }), []);

  const soldIcon = useMemo(() => new L.DivIcon({
    className: 'custom-parcel-marker sold-marker',
    html: `<div class="marker-pin sold-pin"><svg width="14" height="14" viewBox="0 0 14 14"><circle cx="7" cy="7" r="5.5" fill="${STATUS_COLORS.sold.fill}" stroke="#fff" stroke-width="2"/></svg></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  }), []);

  const selectedIcon = useMemo(() => new L.DivIcon({
    className: 'custom-parcel-marker selected-marker',
    html: `<div class="marker-pin selected-pin"><svg width="18" height="18" viewBox="0 0 18 18"><circle cx="9" cy="9" r="7" fill="${STATUS_COLORS.selected.fill}" stroke="${STATUS_COLORS.selected.stroke}" stroke-width="2.5"/><circle cx="9" cy="9" r="3" fill="#fff"/></svg></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  }), []);

  const handleDeselectClick = useCallback(() => {
    onParcelSelect(null);
  }, [onParcelSelect]);

  return (
    <div className="map-container">
      <MapContainer 
        bounds={bounds}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
        scrollWheelZoom={true}
      >
        <LayersControl position="topright">
          <LayersControl.BaseLayer name="Satellite" checked>
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
            />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Terrain">
            <TileLayer
              url="https://stamen-tiles.a.ssl.fastly.net/terrain/{z}/{x}/{y}.jpg"
              attribution='Map tiles by <a href="http://stamen.com">Stamen Design</a>, under <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a>. Data by <a href="http://openstreetmap.org">OpenStreetMap</a>'
            />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Street Map">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer name="Topographic">
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
              attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {/* Fly to highlighted parcel when selected from properties */}
        <FlyToBounds highlightedParcel={highlightedParcel} />

        {/* Reset view control */}
        <ResetViewControl bounds={bounds} onReset={handleDeselectClick} />
        
        {/* Render land parcels as polygons */}
        {parcels.map((parcel) => {
          // Calculate center point for marker
          const centerLat = parcel.coordinates.reduce((sum, coord) => sum + coord[0], 0) / parcel.coordinates.length;
          const centerLng = parcel.coordinates.reduce((sum, coord) => sum + coord[1], 0) / parcel.coordinates.length;
          const isSold = parcel.status === 'sold';
          const priceText = typeof parcel.price === 'number' ? parcel.price.toLocaleString() : String(parcel.price);
          const isHighlighted = highlightedParcel && highlightedParcel.id === parcel.id;

          // Choose marker icon based on state
          let markerIcon = isSold ? soldIcon : availableIcon;
          if (isHighlighted) markerIcon = selectedIcon;

          const statusLabel = isSold
            ? t('properties_sold')
            : parcel.status === 'available'
              ? t('properties_available')
              : t('properties_not_available');

          const statusColor = isSold ? STATUS_COLORS.sold.stroke : parcel.status === 'available' ? '#8B6914' : '#9e2a2b';

          // Determine colors based on status (consistent with legend)
          // Only the selected parcel changes color; all others keep their normal appearance
          const baseColors = isSold ? STATUS_COLORS.sold : STATUS_COLORS.available;
          const activeColors = isHighlighted ? STATUS_COLORS.selected : baseColors;

          return (
            <React.Fragment key={parcel.id}>
              {/* Polygon for land parcel boundary */}
              <Polygon
                positions={parcel.coordinates}
                pathOptions={{
                  color: activeColors.stroke,
                  fillColor: activeColors.fill,
                  fillOpacity: isSold ? 0.3 : isHighlighted ? 0.5 : 0.35,
                  weight: isSold ? 2 : isHighlighted ? 4 : 2.5,
                  opacity: isSold ? 0.6 : 0.85,
                  dashArray: isSold ? '6 4' : null,
                }}
                eventHandlers={{
                  click: () => onParcelSelect(parcel),
                  mouseover: (e) => {
                    e.target.setStyle({
                      fillOpacity: isSold ? 0.45 : 0.55,
                      weight: isSold ? 3 : 4
                    });
                  },
                  mouseout: (e) => {
                    e.target.setStyle({
                      fillOpacity: isSold ? 0.3 : isHighlighted ? 0.5 : 0.35,
                      weight: isSold ? 2 : isHighlighted ? 4 : 2.5
                    });
                  }
                }}
              >
                {/* Permanent label on polygon */}
                <Tooltip 
                  permanent 
                  direction="center" 
                  className={`parcel-label ${isSold ? 'parcel-label-sold' : ''}`}
                >
                  {parcel.name}
                </Tooltip>

                {!isMobile && (
                <Popup>
                  <div className="map-popup">
                    <h3>{parcel.name}</h3>
                    <div className="popup-status" style={{ color: statusColor }}>
                      {statusLabel}
                    </div>
                    {!isSold && (
                      <div className="popup-price">{priceText}</div>
                    )}
                    <div className="popup-meta">
                      <p><strong>{t('map_popup_size')}</strong> {parcel.size}</p>
                      <p><strong>{t('map_popup_location')}</strong> {parcel.location}</p>
                    </div>
                    {!isSold && (
                      <div className="popup-features">
                        <strong>{t('map_popup_features')}</strong>
                        <ul>
                          {parcel.features.filter(f => !f.startsWith('Official name:')).slice(0, 3).map((feature, index) => (
                            <li key={index}>{feature}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </Popup>
                )}
              </Polygon>
              
              {/* Center marker for each parcel */}

              {/* Additional polygons for bundled parcels */}
              {parcel.additionalCoordinates && parcel.additionalCoordinates.map((coords, idx) => (
                <Polygon
                  key={`${parcel.id}-additional-${idx}`}
                  positions={coords}
                  pathOptions={{
                    color: activeColors.stroke,
                    fillColor: activeColors.fill,
                    fillOpacity: isSold ? 0.3 : isHighlighted ? 0.5 : 0.35,
                    weight: isSold ? 2 : isHighlighted ? 4 : 2.5,
                    opacity: isSold ? 0.6 : 0.85,
                    dashArray: isSold ? '6 4' : null,
                  }}
                  eventHandlers={{
                    click: () => onParcelSelect(parcel),
                    mouseover: (e) => {
                      e.target.setStyle({
                        fillOpacity: isSold ? 0.45 : 0.55,
                        weight: isSold ? 3 : 4
                      });
                    },
                    mouseout: (e) => {
                      e.target.setStyle({
                        fillOpacity: isSold ? 0.3 : isHighlighted ? 0.5 : 0.35,
                        weight: isSold ? 2 : isHighlighted ? 4 : 2.5
                      });
                    }
                  }}
                />
              ))}

              <Marker 
                position={[centerLat, centerLng]}
                icon={markerIcon}
                eventHandlers={{
                  click: () => onParcelSelect(parcel)
                }}
              >
                {!isMobile && (
                <Popup>
                  <div className="map-popup-marker">
                    <strong>{parcel.name}</strong>
                    <span className="popup-status-mini" style={{ color: statusColor }}>{statusLabel}</span>
                    {!isSold && <span className="popup-price">{priceText}</span>}
                    <em>{t('map_popup_click_details')}</em>
                  </div>
                </Popup>
                )}
              </Marker>
            </React.Fragment>
          );
        })}
      </MapContainer>

      {/* Legend overlay */}
      <MapLegend />
    </div>
  );
};

export default InteractiveMap;
